import { defineStore } from 'pinia'
import * as Api from './api'
import { useCtaStore } from '@/stores/cta/store'
import { useAudioFileStore } from '@/stores/audioFile/store'
import { useTariffsStore } from '../tariffs/store'
import type { JobResponse } from '../job/api'
import type { RecordPromptResult } from './api'

type RecordPromptFailure = {
  status: 'failure'
  error: any
}

type State = {
  loadingPageData: boolean
  loadingError: boolean
  notFoundError: boolean
  errorObject: any
  recordingStatus: any
  audioFilePlaying: number | null
  showFooter: boolean
}

const getDefaultState = (): State => ({
  loadingPageData: false,
  loadingError: false,
  notFoundError: false,
  errorObject: null,
  recordingStatus: null,
  audioFilePlaying: null,
  showFooter: true,
})

export const useNetworkStore = defineStore('network', {
  state: getDefaultState,

  actions: {
    STARTED_LOADING_PAGE_DATA() {
      this.loadingPageData = true
    },

    FINISHED_LOADING_PAGE_DATA() {
      this.loadingPageData = false
    },

    HANDLE_LOADING_ERROR(errorObject: any) {
      this.loadingPageData = false
      this.loadingError = true
      this.errorObject = errorObject
    },

    HANDLE_CTA_NOT_FOUND_ERROR(errorObject: any) {
      this.loadingPageData = false
      this.loadingError = false
      this.notFoundError = true
      this.errorObject = errorObject
    },

    SET_RECORDING_STATUS(recordingStatus: any) {
      this.recordingStatus = recordingStatus
    },

    AUDIO_FILE_EVENT(event: { playing: boolean; promptId: number }) {
      this.audioFilePlaying = event.playing ? event.promptId : null
    },

    SHOW_FOOTER() {
      this.showFooter = true
    },

    HIDE_FOOTER() {
      this.showFooter = false
    },

    async updateTapiSettings(tapiSettings: any) {
      const result = await Api.updateTapiSettings(tapiSettings)

      if (result.status === 'error') {
        throw result.payload
      }

      useCtaStore().load({ useCache: false })
    },

    async deleteTapiSettings(tapiId: any) {
      const result = await Api.deleteTapiSettings(tapiId)

      if (result.status === 'error') {
        throw result.payload
      }

      useCtaStore().load({ useCache: false })
    },

    async updatePlan(planShortcode: any) {
      const result = await Api.updatePlan(planShortcode)

      if (result.status === 'error') {
        throw result.payload
      }

      useCtaStore().load({ useCache: false })
      useTariffsStore().loadTariffs({ useCache: false })
    },

    // #### ANALYTICS REQUESTS
    async loadAnalyticsLink() {
      const result = await Api.loadAnalyticsLink()

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async activateAnalytics() {
      const result = await Api.activateAnalytics()

      if (result.status === 'error') {
        throw result.payload
      }

      useCtaStore().load({ useCache: false })

      return result.payload
    },

    async deactivateAnalytics() {
      const result = await Api.deactivateAnalytics()

      if (result.status === 'error') {
        throw result.payload
      }

      useCtaStore().load({ useCache: false })

      return result.payload
    },

    async loadAnalyticsDPA() {
      const result = await Api.loadAnalyticsDPA()

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    // #### EXTENSION REQUESTS

    // #### SIMPLE FORWARDING REQUESTS

    // #### EXTENDED FORWARDING REQUESTS
    async createProfile({ extensionId, profileData }: any) {
      const result = await Api.createProfile(extensionId, profileData)

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async updateProfile({ extensionId, profileId, profileData }: any) {
      const result = await Api.updateProfile(
        extensionId,
        profileId,
        profileData
      )

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async deleteProfile({ extensionId, profileId }: any) {
      const result = await Api.deleteProfile(extensionId, profileId)

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async createStep({ extensionId, profileId, stepData }: any) {
      // TODO(Update-Step-Request): Fix network request for selected devices and prompt
      // * Selected Devices and Prompts can not be saved correctly yet
      const result = await Api.createStep(extensionId, profileId, stepData)

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async updateStep({ extensionId, profileId, stepId, stepData }: any) {
      // TODO(Update-Step-Request): Fix network request for selected devices and prompt
      // * Selected Devices and Prompts can not be saved correctly yet
      const result = await Api.updateStep(
        extensionId,
        profileId,
        stepId,
        stepData
      )

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async deleteStep({ extensionId, profileId, stepId }: any) {
      const result = await Api.deleteStep(extensionId, profileId, stepId)

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async saveTimeslots({ profileId, profileData }: any) {
      const result = await Api.saveTimeslots(profileId, profileData)

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    // ##### PROMPT REQUESTS

    async recordPrompt(
      prompt: any
    ): Promise<RecordPromptResult | RecordPromptFailure> {
      this.SET_RECORDING_STATUS(null)

      const onStatusChange = (status: JobResponse['status']) => {
        if (status !== 'completed') {
          this.SET_RECORDING_STATUS(status === 'error' ? 'failure' : status)
        }
      }

      if (prompt.editPrompt) {
        const promptId = useAudioFileStore().audioFile?.id!

        const result = await Api.promptExist(promptId, prompt, onStatusChange)

        if (result.status === 'success') {
          this.SET_RECORDING_STATUS(result.payload.status)

          return result.payload
        }

        this.SET_RECORDING_STATUS('failure')

        return {
          status: 'failure' as const,
          error: result.payload,
        }
      }

      const result = await Api.promptDoesntExist(prompt, onStatusChange)

      if (result.status === 'success') {
        this.SET_RECORDING_STATUS(result.payload.status)

        return result.payload
      }

      this.SET_RECORDING_STATUS('failure')

      return {
        status: 'failure',
        error: result.payload,
      }
    },

    async cancelRecordPrompt() {
      const result = await Api.cancelRecordPrompt()

      if (result.status === 'error') {
        throw result.payload
      }
    },

    // #### OFFICE DESK REQUESTS

    // #### RESET PASSWORD
    async saveResetPassword(data: any) {
      const result = await Api.saveResetPassword(data)

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async resendOfficeDeskEmail(data: any) {
      const result = await Api.resendOfficeDeskEmail(data)

      if (result.status === 'error') {
        throw result.payload
      }
    },

    // #### OTHER
    setPageLoading(payload: any) {
      if (payload) {
        this.STARTED_LOADING_PAGE_DATA()
      } else {
        this.FINISHED_LOADING_PAGE_DATA()
      }
    },

    setShowFooter(payload: any) {
      if (payload) {
        this.SHOW_FOOTER()
      } else {
        this.HIDE_FOOTER()
      }
    },

    audioFileEvent(payload: any) {
      this.AUDIO_FILE_EVENT(payload)
    },

    // #### CFC REQUESTS
    // Tariffs

    // Tutorials

    async loadTutorial(id: string) {
      const result = await Api.loadTutorial(id)

      if (result.status === 'error') {
        this.HANDLE_LOADING_ERROR(result.payload)
      }

      return result.payload
    },
  },
})
